import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { Subject, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ActionService } from 'src/app/core/services/action.service';
import { RouteService } from 'src/app/core/services/route.service';
import {
  CreateTable,
  LoadNavigation,
  LoadTables,
  UpdateTable,
  ValidateTableName,
} from 'src/app/core/state/_actions/table.actions';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import {
  getNavigation,
  getTableOperations,
  getValidateName,
} from 'src/app/core/state/_selectors/table.selector';
import { TableService } from 'src/app/core/state/_services/table.service';
import { remoteFileToBase64 } from 'src/app/core/utils/data-processor.util';
import { DragUpload } from 'src/app/shared/models/components.models';
import {
  AlertService,
  ToastTypes,
} from 'src/app/shared/services/alert.service';
import { icons } from '../../home/application-new/application-new.component';
import { SubSink } from 'subsink';
import { CommonService } from 'src/app/core/services/common.service';

@Component({
  selector: 'app-upsert',
  templateUrl: './upsert.component.html',
  styleUrls: ['./upsert.component.scss'],
})
export class UpsertComponent implements OnInit {
  @Input('data') data: any = {};
  form!: UntypedFormGroup;
  availableTables: Array<{
    title: string;
    id: string;
  }> = [];
  iconBaseURL: string = '/assets/images/icons/default-app-icons/';
  icons: Array<string> = icons;
  dragValue!: DragUpload;
  clearDragUpload = new Subject();
  imageChangedEvent: any = '';
  fileName: string = '';
  newLogoData: any = null;
  deleteLogo = false;
  isUpsertComponent:boolean = true;
  subsink: SubSink = new SubSink();

  constructor(
    private fb: UntypedFormBuilder,
    private modal: NgbModal,
    private route: RouteService,
    private action: ActionService,
    private store: Store,
    private alert: AlertService,
    private tableService: TableService,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadSubscribers();
  }

  get isUpdate() {
    return !!this.data.Key;
  }

  /**
   * Init form
   */
  initForm = () => {
    this.form = this.fb.group({
      Name: [
        this.data.Name,
        [
          Validators.required,
          Validators.minLength(5),
          Validators.pattern(/^[a-zA-Z][a-zA-Z0-9 ]{4,63}$/),
          Validators.maxLength(64),
        ],
      ],
      SingularReference: [
        this.data.SingularReference,
        // [
        //   Validators.required,
        //   Validators.minLength(5),
        //   Validators.pattern(/^[a-zA-Z][a-zA-Z0-9 ]{4,45}$/),
        // ],
      ],
      ApplicationKey: [this.route.getApplicationKey()],
      Id: 0,
      Key: this.data.Key, 
      logo: [this.data.Logo],
      Icon:[this.data.Icon],
      IsIcon:this.data.IsIcon || false,
      IconColor:[this.data.IconColor]
    });
  };

  /**
   * Submit
   */

  onSubmit = () => {
    if (
      this.data?.SingularReference ==
        this.form.controls?.SingularReference.value &&
      this.data.Name == this.form.controls?.Name.value && 
      this.data.Icon == this.form.controls?.Icon.value && 
      this.data.IconColor == this.form.controls?.IconColor.value && 
      !this.newLogoData
    )
      this.close();
    else
      this.isUpdate && this.data.Name === this.form.controls.Name.value
        ? this.onEditSubmit()
        : this.validateTableName();
  };

  validateTableName() {
    this.store.dispatch(
      new ValidateTableName(this.route.getApplicationKey(), this.form.value.Name)
    );
    this.store
      .pipe(
        select(getValidateName),
        filter((table) => !!table[this.route.getApplicationKey()]),
        map((table) => table[this.route.getApplicationKey()]),
        take(1)
      )
      .subscribe((table) => {
        if (table?.IsValid)
          this.isUpdate ? this.onEditSubmit() : this.onAddSubmit();
        else
          this.alert.toast(
            ToastTypes.ERROR,
            'Error',
            `Table "${this.form.value.Name}" already exists`
          );
      });
  }

  onEditSubmit = async () => {
    try {
      const res = await this.action.dispatch(
        OpTypes.UPDATE,
        new UpdateTable(this.form.value),
        getTableOperations
      );
      if (this.newLogoData) {
        this.uploadLogo(res);
      } else{
        this.close(true);
        this.store.dispatch(new LoadNavigation(this.route.getApplicationKey()));
      } 
    } catch (err) {}
  };

  onAddSubmit = async () => {
    try {
      const res = await this.action.dispatch(
        OpTypes.CREATE,
        new CreateTable(this.form.value),
        getTableOperations
      );
      if (this.newLogoData) {
        this.uploadLogo(res);
      } else this.close(true);
    } catch (err) {}
  };

  /**
   * Subscribers
   */
  loadSubscribers = () => {
    this.subsink.add(
    this.store
      .pipe(
        select(getNavigation),
        filter((data) => !!data[this.route.getApplicationKey()])
      )
      .subscribe((data) => {
        this.availableTables = data[this.route.getApplicationKey()]?.map(
          (table) => {
            return {
              title: table.Title.toLowerCase(),
              id: table?.LevelIds?.TableId,
            };
          }
        );
      }));
  };

  /**
   * Drag file event
   */
  dragFileEvent = (event: any) => {
    this.dragValue = event;
    if (event.value.length) {
      this.fileName = event.value[0].name;
      this.imageChangedEvent = `data:image/png;base64,${event.value[0].value}`;
    } else this.imageChangedEvent = '';
  };

  fileChangeEvent(event: any, imageChopper: boolean = true): void {
    this.fileName = event.target.files[0].name;
    if (imageChopper) this.imageChangedEvent = event;
    let formdata = new FormData();
    formdata.append('0', event.target.files[0]);
    formdata.append('applicationKey', this.route.getApplicationKey() + '');
    this.newLogoData = formdata;
  }

  uriToFile = async (inputURL: string, fileName: string = '') => {
    this.deleteLogo = inputURL.includes('delete');
    const inputURI = inputURL.includes('data:')
      ? inputURL
      : (<any>await remoteFileToBase64(inputURL, false)).value;
    const arr = inputURI.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]);
    let n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    this.fileChangeEvent(
      {
        target: {
          files: [
            new File(
              [u8arr],
              fileName ? fileName : inputURL.split('/').slice(-1).join(),
              {
                type: mime,
              }
            ),
          ],
        },
      },
      false
    );
  };

  imageCropped(event: ImageCroppedEvent) {
    this.form.patchValue({
      Icon:'',
      IsIcon:false,
      IconColor: this.commonService.defaultIconColor
    })
    this.uriToFile(event.base64!, this.fileName);
  }

  /**
   * Close modal
   */
  close = (isSaved:boolean = false) => {
    this.modal.dismissAll(isSaved);
  };

  uploadLogo(res:any){
    this.newLogoData.append('applicationTableKey', res.payload.Key);
    this.tableService
      .uploadLogo(this.newLogoData)
      .subscribe(async (res1: any) => {
        try {
          await this.action.dispatch(
            OpTypes.UPDATE,
            new UpdateTable({
              Logo: this.deleteLogo ? '' : res1.FileName,
              Key: res.payload.Key,
              ApplicationKey: this.route.getApplicationKey(),
              Name: res.payload.Name,
              SingularReference: res.payload.SingularReference,
            }),
            getTableOperations
          );
        } catch (err) {}
        this.store.dispatch(
          new LoadNavigation(this.route.getApplicationKey())
        );
        this.store.dispatch(new LoadTables(this.route.getApplicationKey()));
        this.close(true);
      });
  }

  resetLogo = async () => {
    this.form.patchValue({
      logo: '',
      Icon:'',
      IsIcon:false,
      IconColor:this.commonService.defaultIconColor
    })
    try {
      await this.action.dispatch(
        OpTypes.UPDATE,
        new UpdateTable(this.form.value),
        getTableOperations
      );
      this.store.dispatch(new LoadNavigation(this.route.getApplicationKey()));
    } catch (err) {}
  }

  ngOnDestroy(){
    this.subsink.unsubscribe();
  }
}
